import React from 'react'

import { css } from 'design'
import { ButtonProps, buttonStyles } from 'primitives/button'
import { cn } from 'util/cn'

import type { ComposerType } from '../composer'

export const toolbarButtonStyles = css({
  $$toolbarButtonColor: '$colors$grey950',
  color: '$$toolbarButtonColor !important',
  fill: '$$toolbarButtonColor !important',
  stroke: '$$toolbarButtonColor !important',
  background: '$grey100',

  borderColor: 'transparent',

  mixBlendMode: 'multiply',
  minWidth: '$space$6',

  // Prevent children of this button from being event targets
  '> *': {
    pointerEvents: 'none',
  },

  '&[data-trix-active]': {
    background: '$grey200 !important',
  },

  '@hover': {
    '&[data-trix-active]:hover': {
      background: '$grey300 !important',
    },
  },

  '.topic__theme--dark &': {
    mixBlendMode: 'normal',
  },

  // Custom colour variants from design
  '&:disabled': {
    opacity: 0.5,
    $$toolbarButtonColor: 'white',
    background: '$grey100',
    border: '0',
  },

  variants: {
    activeType: {
      message: {},
      note: {
        $$toolbarButtonColor: '$colors$yellow900',

        '&[data-trix-active]': {
          background: '$yellow200 !important',
        },

        '@hover': {
          '&:hover': {
            background: '$yellow300 !important',
            $$toolbarButtonColor: '$colors$yellow950',
          },
        },
      },
    },
    desktop: {
      false: {
        flex: 1,
        minWidth: 'unset',
      },
    },
    toggled: {
      true: {
        border: 'transparent',
      },
    },
    variant: {
      primary: {
        $$toolbarButtonColor: '$colors$grey950',
        '@md': {
          $$toolbarButtonColor: 'white',
        },

        '.topic__theme--dark &': {
          $$toolbarButtonColor: 'white',
        },

        '&:disabled': {
          $$toolbarButtonColor: '$colors$backgroundPrimary',
          background: '$grey400 !important',
        },
      },

      secondary: {},
      tertiary: {},
      danger: {
        $$toolbarButtonColor: '$colors$red950',
      },
      success: {
        $$toolbarButtonColor: '$colors$green950',
      },
      info: {
        $$toolbarButtonColor: '$colors$blue950',
      },
      warning: {
        $$toolbarButtonColor: '$colors$orange950',
      },
      question: {
        $$toolbarButtonColor: '$colors$purple950',
      },
      clean: {},
      blank: {},
      link: {},
    },
  },
  compoundVariants: [
    {
      activeType: 'message',
      toggled: 'true',
      css: {
        $$toolbarButtonColor: 'white',
      },
    },
    {
      activeType: 'note',
      toggled: 'true',
      css: {
        background: '$yellow200 !important',
      },
    },
    {
      activeType: 'note',
      variant: 'primary',
      css: {
        $$toolbarButtonColor: 'white',
        background: '$yellow200 !important',
        border: 'transparent',
        '@hover': {
          '&:hover': {
            $$toolbarButtonColor: 'white',
          },
        },
      },
    },
  ],
})

export type ToolbarButtonProps = {
  activeType?: ComposerType
  desktop?: boolean
} & ButtonProps

export const ToolbarButton = React.forwardRef<
  HTMLButtonElement,
  ToolbarButtonProps
>(
  (
    {
      children,
      className,
      variant = 'tertiary',
      toggled,
      disabled,
      activeType = 'message',
      desktop = true,
      ...props
    },
    ref,
  ) => {
    return (
      <button
        type="button"
        {...props}
        className={cn(
          'topic__toolbarbutton',
          buttonStyles({
            shape: desktop ? 'square' : 'pill',
            toggled,
            variant: desktop ? variant : 'secondary',
          }),
          toolbarButtonStyles({
            activeType,
            desktop,
            toggled,
            variant,
          }),
          className,
        )}
        disabled={disabled}
        ref={ref}
      >
        {children}
      </button>
    )
  },
)

ToolbarButton.displayName = 'ToolbarButton'
